import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TypedMemo } from '../../common/types';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../common/views/LoadingView';
import { openDirectedVideoOf } from '../directedSteps';
import { useCreateDirectedVideoMutation, useGetDirectedVideosQuery } from '../directedVideoApi';
import { directedVideoPath } from '../directedVideoPath';
import { directedMessages as messages } from '../messages/directedMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DirectedVideoOpenerProps {
  accountId: number;
  catalogItemUuid: string;
}

/**
 * The wizard link with no video uuid (the song page entry, the Studio card): it opens the newest
 * directed video of the song that is still edited or being made, or makes a new one (`POST
 * .../directed`, status `cast`; design a1, a4). Then it puts the uuid in the URL with `replace`, so
 * Back goes to the page before and a reload opens the same video. It makes at most one video for
 * one visit. It uses only a list that it read after its mount: a cached list from earlier in the
 * session can still show a ready video as `making` (owner report 2026-10-19).
 */
const DirectedVideoOpenerComponent: React.FC<DirectedVideoOpenerProps> = ({ accountId, catalogItemUuid }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const from = params.get('from');
  const list = useGetDirectedVideosQuery({ accountId, catalogItemUuid }, { refetchOnMountOrArgChange: true });
  const [create] = useCreateDirectedVideoMutation();
  const started = useRef(false);
  const [createError, setCreateError] = useState<unknown>(undefined);
  const [mountedAt] = useState(() => Date.now());

  const open = useCallback(
    (uuid: string) => {
      // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
      void navigate(directedVideoPath(catalogItemUuid, uuid, { accountId, from }), { replace: true });
    },
    [accountId, catalogItemUuid, from, navigate]
  );

  // A list from a read that started before the mount is old: wait for the read of this visit.
  const isFresh = !list.isFetching && list.startedTimeStamp !== undefined && list.startedTimeStamp >= mountedAt;
  const videos = isFresh && list.isSuccess ? list.data?.videos : undefined;
  // A read that was in flight at the mount (the entry reads the list on focus) is shared, not
  // started again. When it ends, the list is still old: read it once more.
  const askedAgain = useRef(false);
  const { isFetching, refetch } = list;
  useEffect(() => {
    if (isFetching || isFresh || askedAgain.current) {
      return;
    }
    askedAgain.current = true;
    refetch().catch(console.error);
  }, [isFetching, isFresh, refetch]);
  useEffect(() => {
    if (videos === undefined || started.current) {
      return;
    }
    started.current = true;
    const existing = openDirectedVideoOf(videos);
    if (existing !== null) {
      open(existing.uuid);
      return;
    }
    create({ accountId, catalogItemUuid })
      .unwrap()
      .then(({ video }) => open(video.uuid))
      .catch((error: unknown) => {
        console.error('Directed video: the new video was not made', error);
        setCreateError(error);
      });
  }, [accountId, catalogItemUuid, create, open, videos]);

  const error = createError ?? (!list.isFetching && list.isError ? list.error : undefined);
  if (error !== undefined) {
    return (
      <div className={styles.fullState}>
        <ErrorView
          error={error}
          retry={() => {
            started.current = false;
            setCreateError(undefined);
            list.refetch().catch(console.error);
          }}
        />
      </div>
    );
  }
  return (
    <div className={styles.fullState} role="status">
      <LoadingView label={intl.formatMessage(messages.opening)} />
    </div>
  );
};

export const DirectedVideoOpener = TypedMemo(DirectedVideoOpenerComponent);
